import { useEffect, useState } from 'react';
import BZAPIService from '../../../services/BZAPIService';
import { hasCachedCount, getCachedCount, missingIds, setCachedCount } from './planInventoryCountCache';

/**
 * Chunk size for POST /bsa/inventory/counts requests. Must stay at or below
 * MAX_IDS in service-rest-api/bsa/inventory/get_ci_counts.py -- that endpoint
 * returns 400 for a larger batch, which apiClient resolves (not rejects), so
 * an unchunked request over the cap silently produced zero counts for the
 * whole page (finding 1). Keep the two constants in sync.
 */
export const COUNTS_BATCH_SIZE = 100;

function chunk(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Device counts for a list of managed products (STRY0038414).
 *
 * Returns a Map of managedProductId -> count, where `null` means the backend
 * could not count that product (no adapter, no count mode, or a CMDB failure):
 * the caller shows nothing rather than an error. An id absent from the Map has
 * not been answered yet.
 *
 * Only ids missing from the session cache are requested, which is what makes
 * returning to a tab free while a page reload re-requests. Missing ids are
 * split into chunks of at most COUNTS_BATCH_SIZE and requested independently,
 * so a large fleet still gets counts, and one failing chunk does not affect
 * the others.
 *
 * `enabled` is `usePlanProjectGate`'s result on every one of today's three
 * call sites (STRY0038414 finding 7): a read-only Prevent user sees no
 * device counts anywhere, not just no action. Deliberate, not incidental --
 * a count spends a live CMDB read, so it is gated on the same
 * project-create permission as the action it accompanies, rather than on a
 * separate, weaker "can view" check.
 */
export function useDeviceCounts(managedProducts, enabled) {
  const [counts, setCounts] = useState(() => new Map());
  const ids = (managedProducts || [])
    .map(mp => mp && mp.id)
    .filter(id => Number.isInteger(id));
  // primitive dependency: a fresh array every render would loop forever
  const idsKey = ids.join(',');

  useEffect(() => {
    if (!enabled || !ids.length) return undefined;

    const snapshot = () => {
      const next = new Map();
      ids.forEach(id => {
        if (hasCachedCount(id)) next.set(id, getCachedCount(id));
      });
      return next;
    };

    const missing = missingIds(ids);
    if (!missing.length) {
      setCounts(snapshot());
      return undefined;
    }

    let cancelled = false;
    chunk(missing, COUNTS_BATCH_SIZE).forEach(idsChunk => {
      BZAPIService.bsaInvCounts(idsChunk)
        .then(res => {
          (res.counts || []).forEach(row => {
            if (Number.isInteger(row.managedProductId)) {
              setCachedCount(row.managedProductId, row.ciCount);
            }
          });
          // a definitive "cannot count this" (no adapter, or no count mode)
          // — cache it so revisiting the tab does not ask again
          (res.unsupported || []).forEach(id => setCachedCount(id, null));
          // `unavailable` ids (missing/disabled product, ServiceNow not
          // configured, or a CMDB read failure) are deliberately left
          // uncached here — transient, so a later mount must re-request them
          // rather than being stuck showing no count for the rest of the
          // session (finding 2+3).
          if (!cancelled) setCounts(snapshot());
        })
        .catch(() => {
          // deliberately NOT cached: a transient failure should retry on the
          // next visit rather than blanking the count for the whole session.
          // Other chunks are independent requests and are unaffected.
          if (!cancelled) setCounts(snapshot());
        });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, idsKey]);

  return counts;
}
